import React, { FC, useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { JsonForms } from '@jsonforms/react';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { CloseIcon } from '@dynamika-shared/ui/icons';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { JsonSchema } from '@jsonforms/core';
import Ajv from 'ajv';
import ajvErrors from 'ajv-errors';
import { FormState } from '@restore/ValidationSpecialistQuestionnaires/ValidationSpecialistQuestionnaires.types';
import { combineAntView } from '@components/uploadForm';
import { useValidationSpecialistQuestionnairesSlice } from '@restore/ValidationSpecialistQuestionnaires/ValidationSpecialistQuestionnaires.slice';
import { useValidationSpecialistSubjectsSlice } from '@restore/ValidationSpecialistSubjects/ValidationSpecialistSubjects.slice';
import { isVSSchema } from '@utils/validationSpecialist';
import './index.scss';

interface VSParticipantFormEditProps {
  uploadItemId: string;
  setFormMode: (formMode: 'closed' | 'view' | 'edit') => void;
}

const ajv = ajvErrors(
  new Ajv({ allErrors: true, useDefaults: true, strict: false })
);

const populateMissingBooleans = (
  schema: JsonSchema,
  data: FormState
): FormState => {
  const result: FormState = data ? { ...data } : {};

  if (!schema?.properties) return result;
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (
      typeof prop === 'object' &&
      prop?.type === 'boolean' &&
      result[key] === undefined
    ) {
      result[key] = false;
    }
  }

  return result;
};

export const VSParticipantFormEdit: FC<VSParticipantFormEditProps> = ({ uploadItemId, setFormMode }) => {
  const [ subjectDetails ] = useValidationSpecialistSubjectsSlice((s) => [s.subjectDetails]);
  const [
    schemas,
    edtfData,
    updateEDTFData,
    updateParticipantForm,
    selectedSchema,
    selectEDTFSchema,
  ] = useValidationSpecialistQuestionnairesSlice((state, actions) => [
    state.schemas,
    state.edtfData,
    actions.updateEdtfData,
    actions.updateParticipantForm,
    state.selectedSchema,
    actions.selectEDTFSchema,
  ]);

  const { clinicalTrialId } = useParams<{ clinicalTrialId: string }>();

  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const validateForm = (edtfData: FormState) => {
    if (!selectedSchema?.schema) {
      setIsFormValid(false);
      return;
    }
    const isValid = ajv.validate(selectedSchema.schema, edtfData);
    setIsFormValid(isValid);
  };
  
  const handleFormChange = ({ data }: { data: FormState }) => {
    updateEDTFData(data);
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }

    if (isFormTouched) {
      validateForm(data);
    } else {
      setIsFormTouched(true);
      validateForm(data);
    }
  };
  
  useEffect(() => {
    const vsSchema = schemas.find(isVSSchema);

    if (vsSchema && !selectedSchema) {
      const initialData = populateMissingBooleans(vsSchema.schema, {});

      updateEDTFData(initialData);
      selectEDTFSchema(vsSchema);
      setIsInitialized(false);
    }
  }, [schemas, selectedSchema]);
  
  const jsonFormAntView = useMemo(() => combineAntView(), [clinicalTrialId]);

  const handleCloseDrawer = () => setFormMode('closed');

  const onCloseDrawer = () => {
    updateEDTFData(null);
    handleCloseDrawer();
  };

const handleSubmitForm = async () => {
  if (!clinicalTrialId || !edtfData || !selectedSchema) return;
    try {
      updateParticipantForm({
        clinicalTrialId,
        data: edtfData,
        uploadItemId,
        questionnaireId: selectedSchema.id,
      });
    } finally {
      handleCloseDrawer();
    }
};

  return (
    <Drawer
      title={
        <div className='flex flex-col items-center w-full'>
          <div className='flex justify-between border-brightGray w-full'>
            <h2 className='flex leading-32 mb-[24px] text-2.4rem truncate'>
              <span className='text-spanishGray'>Fill Participant Form for</span>

              <TruncatedText
                text={subjectDetails?.patientCode}
                truncate
                tooltip
                className={'max-w-[400px] ml-1'}
              />
            </h2>

            <CloseIcon
              className='absolute right-2 top-2 h-3 w-3 cursor-pointer'
              onClick={onCloseDrawer}
            />
          </div>
        </div>
      }
      id='participant-form-drawer'
      classNames={{
        header: '!p-0',
        body: 'overflow-y-hidden !px-0 !py-[32px]',
        content: 'rounded-[24px]',
        footer: '!p-0',
      }}
      closeIcon={null}
      open={true}
      width={928}
      placement='right'
      footer={
        <div className='flex justify-between mt-5'>
          <Button
            className='w-[48%] flex-none shrink-0'
            onClick={onCloseDrawer}
          >
            Close
          </Button>
          <Button
            type='primary'
            className='w-[48%] flex-none shrink-0'
            onClick={isFormValid ? handleSubmitForm : undefined}
            disabled={!isFormValid}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className='relative h-full'>
        <div id='participantFormGifGroup' />
        {selectedSchema?.schema && (
          <JsonForms
            ajv={ajv}
            key={selectedSchema.id}
            schema={selectedSchema.schema}
            uischema={selectedSchema.uiSchema}
            data={edtfData ?? {}}
            renderers={jsonFormAntView.renderers}
            cells={jsonFormAntView.cells}
            onChange={handleFormChange}
            validationMode={isFormTouched ? 'ValidateAndShow' : 'NoValidation'}
          />
        )}
      </div>
    </Drawer>
  );
};
